import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import "../../App.css";
import * as yup from "yup";
import { axiosInstance } from "../../utils/commonInstance";
import { convertToBase64 } from "../../utils/commonFunction";
import * as moment from "moment";
import { showToast } from "../../hooks/showToast";
import { checkObjectForNullUndefinedOrEmpty } from "../../utils/commonFunction";
import { BackButtonModal } from "../../components/BackButtonModal";
import { Eye, EyeOff } from "feather-icons-react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import AttendanceRow from "../../components/AttendanceRow";

const AttendanceRequestForm = ({ toggleFormFillrter, setGlobalFilter, manualFetchData, isView, isEdit, previousData }) => {
  const [modal, setModal] = useState({
    isOpen: false,
  });
  const [securityGuardOptions, setSecurityGuardOptions] = useState([]);
  const [requestDate, setRequestDate] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const dateInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userSchema = yup.object().shape({
    user_id: yup.number().required("Please Select Security Guard Name").typeError("Please Select Security Guard Name"),
    request_type: yup.string().required("Request Type is required"),
    request_reason: yup.string().required("Please Enter Reason").max(200, "Reason cannot exceed 200 characters"),
    request_date: yup.string().when("request_type", {
      is: "Update Existing Attendance",
      then: (schema) => schema.required("Please Select Date").typeError("Please Select Date"),
      otherwise: (schema) => schema.nullable(true),
    }),
    attendance_request: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.mixed().nullable(),
          type: yup.string().nullable(),
          check_in: yup.string().required("Please Select Check In Date & Time"),
          check_out: yup
            .string()
            .required("Please Select Check Out Date & Time")
            .test("is-after-checkin", "Please Select Checkout Time Greater Than Checkin Time", function (value) {
              const { check_in } = this.parent;
              if (!check_in || !value) return true;
              const checkIn = moment(check_in, "DD-MM-YYYY HH:mm:ss").toDate();
              const checkOut = moment(value, "DD-MM-YYYY HH:mm:ss").toDate();

              return checkOut > checkIn;
            }),
        })
      )
      .min(1, "At least one attendance entry is required"),
  });

  const {
    register,
    handleSubmit,
    trigger,
    reset,
    control,
    setValue,
    getValues,
    formState: { errors },
    setError,
    field = {},
    index,
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      request_type: "Create Attendance",
      attendance_request: [{ id: "", type: "Both", check_in: "", check_out: "" }],
      ...previousData,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "attendance_request",
  });

  const selectedType = useWatch({
    control,
    name: `attendance_request.${index}.type`,
    defaultValue: field.type || "Both",
  });

  useEffect(() => {
    async function fetchData() {
      const response = await axiosInstance.get(`web/guardList`);
      setSecurityGuardOptions(response?.data?.data);
    }
    fetchData();
  }, []);

  const onSubmit = (data) => {
    setFormData(data);
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!formData) return;
    setIsSubmitting(true);
    const isUpdate = formData?.request_type === "Update Existing Attendance";

    const params = {
      user_id: formData?.user_id,
      request_type: formData?.request_type,
      request_reason: formData?.request_reason,
      attendance_request: formData.attendance_request.map((item) => ({
        id: item?.id || null,
        type: item?.type,
        check_in: item?.check_in,
        check_out: item?.check_out,
      })),
      mode_of_login: "Web",
    };

    const requestData = isUpdate ? { ...params, request_date: formData?.request_date } : params;

    try {
      const response = await axiosInstance.post(`web/attdenaceRequestForm`, requestData);

      if (response?.data?.statusCode !== 200) {
        showToast(response?.data?.message, "error");
        setIsSubmitting(false);
        return;
      }

      toggleFormFillrter(false);
      manualFetchData();
      showToast(response?.data?.message, "success");
      setConfirmModalOpen(false);
      clearErrors();
    } catch (error) {
      showToast(error?.response?.data?.message || "Submission failed", "error");
      setIsSubmitting(false);
    }
  };

  const handleModalopen = () => {
    setModal((prevData) => ({
      ...prevData,
      isOpen: true,
    }));
  };

  const handleClose = () => {
    setModal((prevData) => ({
      ...prevData,
      isOpen: false,
    }));
  };

  const userId = watch("user_id");
  const requestType = watch("request_type");
  const createdDate = watch("request_date");

  useEffect(() => {
    const resetFormAndRows = () => {
      setValue("request_date", "");
      remove();
      append({ id: "", check_in: "", check_out: "" });
    };

    if (requestType === "Create Attendance" && !isView) {
      resetFormAndRows();
    }

    const fetchData = async () => {
      if (userId && createdDate && requestType !== "Create Attendance" && !isView) {
        try {
          const response = await axiosInstance.get(`web/attdenaceRequestEdit?user_id=${userId}&request_date=${createdDate}`);
          const resData = response?.data?.data;

          if (response?.data?.statusCode !== 200) {
            showToast(response?.data?.message, "error");
            return;
          }

          remove();

          resData?.forEach((item) => {
            append({
              id: item.id || null,
              check_in: item.check_in || "",
              check_out: item.check_out || "",
            });
          });
        } catch (error) {
          showToast(error?.response?.data?.message || "Failed to fetch data", "error");
        }
      }
    };

    fetchData();
  }, [requestType, createdDate, userId]);

  useEffect(() => {
    async function setFiles() {
      try {
        if (isView) {
          const response = await axiosInstance.get(`web/attdenaceRequestView?id=${previousData?.id}`);
          const resData = response?.data?.data;

          if (response?.data?.statusCode !== 200) {
            showToast(response?.data?.message, "error");
            return;
          }

          const formattedRequestDate = moment(resData?.request_date, "DD-MM-YYYY").format("DD-MM-YYYY");

          setValue("user_id", resData?.user_id);
          setValue("request_type", resData?.request_type);
          setValue("request_date", formattedRequestDate);
          setValue("request_reason", resData?.request_reason);

          if (Array.isArray(resData?.attendance_request)) {
            reset({
              user_id: resData?.user_id,
              request_type: resData?.request_type,
              request_date: formattedRequestDate,
              request_reason: resData?.request_reason,
              attendance_request: resData.attendance_request.map((item) => ({
                id: item.id || null,
                type: item.type || "",
                check_in: item.check_in || "",
                check_out: item.check_out || "",
              })),
            });
          }
        }
      } catch (error) {
        showToast(error?.data?.message || "Something went wrong", "error");
      }
    }

    setFiles();
  }, [isView, previousData, setValue, reset]);

  const handleFilterReset = () => {
    reset({
      user_id: "",
      request_type: "Create Attendance",
      request_date: "",
      request_reason: "",
      attendance_request: [{ id: "", check_in: "", check_out: "" }],
    });
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };
  const maxDate = getYesterdayDate();

  return (
    <>
      <section className="userlist-newuser content mobile">
        <div className="d-flex justify-content-end align-items-center mb-4 gap-3 flex-wrap">
          <h3 className="mb-0">
            <strong>{isView ? "View" : requestType === "Update Existing Attendance" ? "Update" : "Create"} Attendance</strong>
          </h3>
          <button
            onClick={() => {
              !isView ? handleModalopen() : toggleFormFillrter();
              setGlobalFilter({ selectFillter: "", search: "" });
            }}
            className="btn btn-lg btn-primary px-md-5 px-4 addlistuser"
          >
            Back
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="user-details">
            <div className="card">
              <div className="row card-body justify-content-around py-md-5 py-4">
                <div className="col-lg-5 col-md-6 mb-3">
                  <div className="form-group">
                    <label htmlFor="guardName" className="form-label">
                      Security Guard Name<span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="user_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={securityGuardOptions?.map((ele) => ({
                            value: ele.id,
                            label: ele.username,
                          }))}
                          isDisabled={isView}
                          placeholder="Select Guard"
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption?.value);
                          }}
                          value={
                            securityGuardOptions
                              ?.map((ele) => ({
                                value: ele.id,
                                label: ele.username,
                              }))
                              .find((opt) => opt.value === field.value) || null
                          }
                          isSearchable
                        />
                      )}
                    />
                  </div>
                  {errors.user_id && <p className="text-danger">{errors.user_id.message}</p>}
                </div>
                <div className="col-lg-5 col-md-6 mb-3">
                  <div className="form-group">
                    <label className="form-label d-block">
                      Request Type:<span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input type="radio" className="form-check-input" id="create" value="Create Attendance" {...register("request_type")} disabled={isView} />
                    <label className="form-check-label" htmlFor="create">
                      Create
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input type="radio" className="form-check-input" id="update" value="Update Existing Attendance" {...register("request_type")} disabled={isView} />
                    <label className="form-check-label" htmlFor="update">
                      Update Existing Attendance
                    </label>
                  </div>
                  {errors.request_type && <p className="text-danger">{errors.request_type.message}</p>}
                </div>
                {requestType === "Update Existing Attendance" && (
                  <div className="col-lg-5 col-md-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="date" className="form-label">
                        Date:<span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="request_date"
                        control={control}
                        rules={{ required: "Date is required" }}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            selected={field.value ? moment(field.value, "DD-MM-YYYY").toDate() : null}
                            onChange={(date) => {
                              const formattedDate = moment(date).format("DD-MM-YYYY");
                              field.onChange(formattedDate);
                              setRequestDate(formattedDate);
                            }}
                            dateFormat="dd-MM-yyyy"
                            maxDate={maxDate}
                            className="form-control py-2 w-100"
                            disabled={isView}
                            placeholderText="dd-mm-yyyy"
                          />
                        )}
                      />
                    </div>
                    {errors.request_date && <p className="text-danger">{errors.request_date.message}</p>}
                  </div>
                )}
                <div className={`col-lg-5 col-md-6 mb-3 ${requestType === "Update Existing Attendance" ? "col-lg-5" : "col-lg-11"}`}>
                  <div className="form-group">
                    <label htmlFor="reason" className="form-label">
                      Reason:<span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="reason"
                      className={`form-control ${errors.request_reason ? "is-invalid" : ""}`}
                      rows="3"
                      {...register("request_reason", {
                        required: "Reason is required",
                      })}
                      disabled={isView}
                    />
                  </div>
                  {errors.request_reason && <p className="text-danger">{errors.request_reason.message}</p>}
                </div>
                <h6>
                  Attendance Record<span className="text-danger">*</span>
                </h6>
                <table className="table table-bordered text-center">
                  <thead className="table-light">
                    <tr>
                      <th>Type</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => (
                      <AttendanceRow key={field.id} field={field} index={index} register={register} control={control} errors={errors} isView={isView} requestType={requestType} trigger={trigger} />
                    ))}
                  </tbody>
                </table>
                <div className="col-12 mt-lg-5 mt-4 d-sm-inline-flex justify-content-sm-center d-grid gap-3">
                  <button disabled={isView} className="btn btn-lg btn-primary px-5" type="submit">
                    {requestType === "Update Existing Attendance" ? "UPDATE" : "SUBMIT"}
                  </button>
                  <button class="btn btn-lg btn-primary px-5 mx-xl-4" onClick={handleFilterReset} disabled={isView} type="button">
                    CLEAR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
        <BackButtonModal isOpen={modal?.isOpen} handleClose={handleClose} toggleFormFillrter={toggleFormFillrter} />
        {confirmModalOpen && (
          <div className="custom-modal-backdrop">
            <div className="custom-modal">
              <div className="custom-modal-dialog">
                <div className="custom-modal-content">
                  <div className="custom-modal-body m-0">
                    <p>Are You Sure, You Want To Save The Attendance Request?</p>
                  </div>
                  <div className="col-12 text-center d-inline-flex justify-content-center gap-3">
                    <button className="btn btn-lg btn-primary px-5" onClick={handleConfirmSubmit} disabled={isSubmitting}>
                      YES
                    </button>
                    <button className="btn btn-lg btn-primary px-5" onClick={() => setConfirmModalOpen(false)}>
                      NO
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default AttendanceRequestForm;
